import { ProcessGeneral } from 'api/endpoints/processes';
import { ProcessInstanceGeneral } from 'api/endpoints/processInstance';
import BaseRepo from 'container/BaseRepo';
import { buildBadRequestApiError } from 'container/helpers/errors';
import { ApiResponseAsync } from 'container/models';
import { PatchTyped } from 'container/patch';
import { SearchRequestTyped } from 'container/search';

import { IAdjacentDocumentsRepo } from '../services/IAdjacentDocuments';
import { AdjacentDocument, AdjacentDocumentBase } from './../typing';

export class AdjacentDocumentsRepo
  extends BaseRepo
  implements IAdjacentDocumentsRepo
{
  create = async (
    draft: ProcessGeneral,
    request: AdjacentDocumentBase
  ): ApiResponseAsync<AdjacentDocument> => {
    const link = draft._links?.['createAdjacentDocument'];
    if (!link) {
      return buildBadRequestApiError(
        new Error(
          'link "create Adjacent document" is not available in process draft'
        )
      );
    }

    return this.helpers.handlePost(link.href, request);
  };

  search = async (
    draft: ProcessGeneral,
    request: SearchRequestTyped<AdjacentDocumentBase>
  ): ApiResponseAsync<AdjacentDocument[]> => {
    const link = draft._links?.['searchAdjacentDocuments'];
    if (!link) {
      return buildBadRequestApiError(
        new Error(
          'link "search Adjacent documents" is not available in process draft'
        )
      );
    }

    return this.helpers.executeSearch(link, request, {
      convertRequestToSnakeCase: true,
    });
  };

  getActiveAdjacentDocuments = async (
    instance: ProcessInstanceGeneral
  ): ApiResponseAsync<AdjacentDocument[]> => {
    const link = instance._links?.['getAdjacentDocuments'];
    if (!link) {
      return buildBadRequestApiError(
        new Error(
          'link "get active Adjacent documents" is not available in process draft'
        )
      );
    }

    return this.helpers.handleGet(link.href);
  };

  update = async (
    document: AdjacentDocument,
    patch: PatchTyped<AdjacentDocumentBase>
  ): ApiResponseAsync<AdjacentDocument> => {
    const link = document._links?.['update'];
    if (!link) {
      return buildBadRequestApiError(
        new Error('link "update" is not available in Adjacent document')
      );
    }

    return this.helpers.executePatch(link, patch, true);
  };

  activate = async (
    document: AdjacentDocument
  ): ApiResponseAsync<AdjacentDocument> => {
    if (document.isActive) return document;

    const link = document._links?.['activate'];
    if (!link) {
      return buildBadRequestApiError(
        new Error('link "activate" is not available in Adjacent document')
      );
    }

    return this.helpers.handlePost(link.href, {});
  };

  deactivate = async (
    document: AdjacentDocument
  ): ApiResponseAsync<AdjacentDocument> => {
    if (!document.isActive) return document;

    const link = document._links?.['deactivate'];
    if (!link) {
      return buildBadRequestApiError(
        new Error('link "deactivate" is not available in Adjacent document')
      );
    }

    return this.helpers.handlePost(link.href, {});
  };

  delete = async (document: AdjacentDocument): ApiResponseAsync<void> => {
    const link = document._links?.['delete'];
    if (!link) {
      return buildBadRequestApiError(
        new Error('link "delete" is not available in  Adjacent document')
      );
    }

    return this.helpers.handleDelete(link.href);
  };
}
